import type {
  MiAttackCluster,
  MiCpcDay,
  MiFocusCluster,
  MiHeaderStat,
  MiKwCluster,
  MiLandingBlueprintItem,
  MiOfferUsage,
  MiOverviewMetric,
  MiSeoKeyword,
  MiSeoMetric,
  MiSignalGroup,
  MiSwotItem,
  MiTabDef,
} from './types';

export const MI_HEADER_STATS: MiHeaderStat[] = [
  { label: 'Data sources analyzed', value: '1,847' },
  { label: 'Competitors tracked', value: '8' },
  { label: 'Last updated', value: '2 hours ago', accent: true },
];

export const MI_TABS: MiTabDef[] = [
  { id: 'overview', label: 'Market overview' },
  { id: 'ads', label: 'Google Ads' },
  { id: 'seo', label: 'Local SEO' },
  { id: 'segments', label: 'Segment Explorer' },
];

export const MI_OVERVIEW_METRICS: MiOverviewMetric[] = [
  { label: 'Total competitors', value: '8', delta: '+2 new this quarter', tone: 'success' },
  { label: 'Market review velocity', value: '+47/mo', delta: 'Your share: 6%', tone: 'neutral' },
  { label: 'Avg market ad spend', value: '$1.2K', delta: 'You: $1.8K (above avg)', tone: 'success' },
  { label: 'Local search volume', value: '14.2K', delta: '↑ 12% vs last quarter', tone: 'success' },
];

export const MI_SWOT: MiSwotItem[] = [
  {
    title: 'Strengths',
    tone: 'success',
    items: [
      'High average review volume across local leaders',
      'Strong local SEO foundations in main zip codes',
      'Bilingual accessibility (Spanish) in top clinics',
    ],
  },
  {
    title: 'Weaknesses',
    tone: 'terra',
    items: [
      'Lack of price transparency in initial consultations',
      'Fragmented emergency coverage during weekends',
      'Old/non-responsive websites leading to high bounce',
    ],
  },
  {
    title: 'Opportunities',
    tone: 'info',
    items: [
      'White space in high-end cosmetic (Veneers focus)',
      'Untapped evening/weekend slots for working professionals',
      'Membership plan adoption lag among competition',
    ],
  },
  {
    title: 'Threats',
    tone: 'gold',
    items: [
      'Private equity SEO rollups consolidating market share',
      'Skyrocketing CPCs in Implants from national aggregators',
      'Aggressive review velocity at 2-3 key local players',
    ],
  },
];

export const MI_COMMENTARY = {
  happening: [
    'Aggressive expansion in North-East quadrant by DSOs.',
    "CPC for 'Invisalign' spiked 45% in the last month.",
    'Spanish-speaking patients are 3x underserved in your area.',
  ],
  responding: [
    'Shifted 15% of budget from Invisalign to Emergency keywords where CPC dropped.',
    "Launched Spanish-language ad variants targeting 'Dentista' cluster.",
    'Increased weekend ad scheduling to exploit competitor coverage gaps.',
  ],
  recommended: [
    'Consider adding Saturday hours — 3 competitors recently did.',
    'Price transparency page could capture 850+ monthly searches.',
  ],
};

export const MI_SIGNALS: MiSignalGroup[] = [
  {
    label: 'Offer prevalence',
    items: [
      { name: '$99 New Patient', value: 72 },
      { name: 'Free Consultation', value: 45 },
      { name: 'Whitening Bundle', value: 38 },
    ],
  },
  {
    label: 'Booking maturity',
    items: [
      { name: 'Online Booking', value: 64 },
      { name: 'Live Chat', value: 28 },
      { name: 'Same-Day Appt', value: 52 },
    ],
  },
  {
    label: 'Digital presence',
    items: [
      { name: 'Mobile Optimized', value: 58 },
      { name: 'Active Blog', value: 42 },
      { name: 'Video Content', value: 35 },
    ],
  },
  {
    label: 'Insurance posture',
    items: [
      { name: 'PPO Accepting', value: 85 },
      { name: 'In-House Plans', value: 38 },
      { name: 'Fee Transparency', value: 22 },
    ],
  },
  {
    label: 'Review patterns',
    items: [
      { name: 'Respond to Reviews', value: 67 },
      { name: 'Video Testimonials', value: 25 },
      { name: '5-Star Avg', value: 48 },
    ],
  },
];

export const MI_KW_CLUSTERS: MiKwCluster[] = [
  { keyword: 'Implants', share: 92, tone: 'ai' },
  { keyword: 'Emergency', share: 85, tone: 'terra' },
  { keyword: 'Cosmetic', share: 68, tone: 'info' },
  { keyword: 'Invisalign', share: 74, tone: 'gold' },
];

export const MI_CPC_DAYS: MiCpcDay[] = [
  { day: 'Mon', value: 4.2 },
  { day: 'Tue', value: 3.8 },
  { day: 'Wed', value: 5.1 },
  { day: 'Thu', value: 4.7 },
  { day: 'Fri', value: 6.2 },
  { day: 'Sat', value: 7.8 },
  { day: 'Sun', value: 3.1 },
];

export const MI_OFFERS: MiOfferUsage[] = [
  { offer: 'Loss Leader ($99)', count: 6 },
  { offer: 'Same-Day CTA', count: 5 },
  { offer: 'Free Cleaning', count: 3 },
  { offer: 'CPC Discount', count: 2 },
];

export const MI_FOCUS: MiFocusCluster[] = [
  { label: 'Weekend Emergency', tone: 'terra', reason: 'High intent, low CPC on weekends' },
  { label: 'Full Arch / All-on-4', tone: 'gold', reason: 'High revenue, competitors weak' },
  { label: 'Pediatric Spanish', tone: 'info', reason: 'Untapped bilingual segment' },
  { label: 'Low-Comp Cosmetic', tone: 'ai', reason: 'Veneers cluster underserved' },
  { label: 'Local Veneer Specialist', tone: 'success', reason: 'Trending search, no dominant player' },
];

export const MI_ATTACK_CLUSTERS: MiAttackCluster[] = [
  { name: 'Same-Day Tooth Repair', value: 85 },
  { name: 'All-on-4 Specialist', value: 70 },
];

export const MI_BUDGET_SPLIT = [
  { label: 'IMPLANTS 60%', flex: 60, tone: 'ai' as const },
  { label: 'EMRG 25%', flex: 25, tone: 'gold' as const },
  { label: '15%', flex: 15, tone: 'lav' as const },
];

export const MI_AD_BLUEPRINT = [
  "Local SPN: 15 campaigns per 'Near Me' variant",
  'Mobile-first landing + booking interface',
  'High-definition patient results gallery',
];

export const MI_LANDING_BLUEPRINT: MiLandingBlueprintItem[] = [
  { label: 'Before/After Gallery Asset', active: true },
  { label: 'Insurance-Check Modal', active: true },
  { label: 'Dr. Bio Video Snippet', active: true },
  { label: 'Live Chatbot Support', active: true },
  { label: '3D Scan Visualizer', active: false },
];

export const MI_SEO_METRICS: MiSeoMetric[] = [
  { label: 'GBP category presence', tags: ['Dentist', 'Cosmetic', 'Emergency'], tone: 'ai' },
  { label: 'Review advantage', value: '+12.4%', sub: 'vs market avg' },
  { label: 'Content freshness index', value: '68', sub: '/ 100' },
  { label: 'Common local gaps', tags: ['No Sunday Hours', 'Price Transparency'], tone: 'gold' },
];

export const MI_SEO_KEYWORDS: MiSeoKeyword[] = [
  {
    keyword: 'Emergency Dentist [City]',
    intent: 'High',
    difficulty: '40%',
    page: 'Service Page',
    note: 'High volume, low competition',
  },
  {
    keyword: 'Invisalign Cost Near Me',
    intent: 'Research',
    difficulty: '65%',
    page: 'Landing Page',
    note: 'Price transparency drives leads',
  },
  {
    keyword: 'Same Day Dental Crown',
    intent: 'High',
    difficulty: '35%',
    page: 'Service Page',
    note: 'Growing 18% QoQ',
  },
  {
    keyword: 'Pediatric Dentist Spanish',
    intent: 'High',
    difficulty: '15%',
    page: 'Landing Page',
    note: 'Almost zero competition',
  },
];

export const MI_SEO_STRATEGY = {
  content: [
    "Update GBP service descriptions with 'Emergency' keywords",
    'Create neighborhood-specific landing pages',
    'Add Spanish-language practice overview to GBP',
  ],
  pages: [
    'Dental Financing & Cost page',
    'Neighborhood-specific landing pages',
    'Implant cost guide for [City]',
  ],
  watch: ['Competitor neighborhood pages gaining traction'],
};

export const MI_SEGMENTS = ['Family', 'Cosmetic', 'Implants', 'Emergency', 'Pediatric', 'Bilingual'];

export const MI_SEGMENT_STATS = [
  { label: 'Segment size', value: '~4,200', sub: 'households in radius' },
  { label: 'Top driver', value: 'Trust', sub: 'Reviews & referrals matter most' },
  { label: 'Est. value', value: '$1.2K', sub: 'LTV average' },
];
